import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Vote, Check, Lock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHOICES = [
  { key: "yes", label: "نعم", bar: "bg-gold", activeCls: "border-gold bg-gold text-navy" },
  { key: "no", label: "لا", bar: "bg-red-400", activeCls: "border-red-400 bg-red-400 text-white" },
  { key: "abstain", label: "امتناع", bar: "bg-slate-400", activeCls: "border-slate-400 bg-slate-400 text-white" },
];

export default function Votes() {
  const { user } = useAuth();
  const [votes, setVotes] = useState(null);
  const [casting, setCasting] = useState(null);

  const load = () => api("/votes").then(setVotes).catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
  }, []);

  const cast = async (voteId, choice) => {
    setCasting(`${voteId}-${choice}`);
    try {
      await api(`/votes/${voteId}/cast`, { method: "POST", body: JSON.stringify({ choice }) });
      toast.success("تم تسجيل صوتك");
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCasting(null);
    }
  };

  return (
    <div data-testid="votes-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <h1 className="mb-2 flex items-center gap-3 font-amiri text-4xl text-navy sm:text-5xl">
          <Vote className="h-8 w-8 text-gold-dark" />
          التصويتات
        </h1>
        <p className="mb-8 text-muted-foreground">قرارات الاتحاد تُتخذ هنا — نعم، لا، أو امتناع. صوت واحد لكل عضو مفعَّل.</p>
      </motion.div>

      {!user.is_active && (
        <div className="mb-8 flex items-center gap-4 rounded-2xl border border-amber-500/40 bg-amber-50 p-6" data-testid="vote-permission-notice">
          <Lock className="h-6 w-6 shrink-0 text-amber-500" />
          <p className="text-sm leading-7 text-amber-700">
            حسابك بانتظار التفعيل. يمكنك الاطلاع على التصويتات ونتائجها، لكن المشاركة تتاح بعد تفعيل الإدارة لحسابك.
          </p>
        </div>
      )}

      <div className="space-y-6" data-testid="votes-list">
        {votes === null ? (
          <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>
        ) : votes.length === 0 ? (
          <motion.div
            className="rounded-2xl border border-dashed border-gold/40 py-20 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Vote className="mx-auto mb-4 h-10 w-10 text-gold/50" />
            <p className="font-amiri text-2xl text-muted-foreground">لا توجد تصويتات حالياً</p>
          </motion.div>
        ) : (
          votes.map((vote, i) => (
            <motion.div
              key={vote.vote_id}
              data-testid={`vote-card-${vote.vote_id}`}
              className="rounded-2xl border border-gold/25 bg-white p-6 shadow-sm sm:p-8"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: Math.min(i * 0.08, 0.5), ease: EASE }}
            >
              <div className="mb-2 flex items-start justify-between gap-4">
                <h2 className="font-amiri text-2xl text-navy sm:text-3xl">{vote.title}</h2>
                <span
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs font-bold ${
                    vote.is_open ? "border-gold/50 bg-gold/10 text-gold-dark" : "border-slate-400/50 bg-slate-100 text-slate-500"
                  }`}
                  data-testid={`vote-status-${vote.vote_id}`}
                >
                  {vote.is_open ? "مفتوح" : "مغلق"}
                </span>
              </div>
              {vote.description && <p className="mb-4 whitespace-pre-wrap leading-8 text-muted-foreground">{vote.description}</p>}
              <p className="mb-6 text-xs text-muted-foreground">
                طرحه {vote.created_by} — {format(new Date(vote.created_at), "d MMMM yyyy", { locale: ar })} — {vote.total} صوت
              </p>

              <div className="mb-6 grid grid-cols-3 gap-3">
                {CHOICES.map((c) => {
                  const isMine = vote.my_choice === c.key;
                  const disabled = !user.is_active || !vote.is_open || casting !== null;
                  return (
                    <button
                      key={c.key}
                      onClick={() => cast(vote.vote_id, c.key)}
                      disabled={disabled}
                      data-testid={`vote-${c.key}-${vote.vote_id}`}
                      className={`flex items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-bold transition-[transform,background-color,border-color,color] duration-300 ${
                        isMine
                          ? c.activeCls
                          : "border-navy/15 bg-white text-navy hover:border-gold hover:bg-gold/5"
                      } ${disabled && !isMine ? "cursor-not-allowed opacity-40" : "hover:-translate-y-0.5"}`}
                    >
                      {isMine && <Check className="h-4 w-4" />}
                      {casting === `${vote.vote_id}-${c.key}` ? "..." : c.label}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-3" data-testid={`vote-results-${vote.vote_id}`}>
                {CHOICES.map((c) => {
                  const count = vote.counts[c.key] || 0;
                  const pct = vote.total ? Math.round((count / vote.total) * 100) : 0;
                  return (
                    <div key={c.key} className="flex items-center gap-3">
                      <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">{c.label}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <motion.div
                          className={`h-full rounded-full ${c.bar}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.9, ease: EASE }}
                        />
                      </div>
                      <span className="w-16 shrink-0 text-left text-xs text-muted-foreground" dir="ltr">
                        {count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
